import React, { useEffect, useMemo, useReducer, useState } from 'react'
import VerticalGroup from '../../atoms/verticalgroup/VerticalGroup'
import RoundResultsSummary from './RoundResultsSummary'
import RoundResultsMarketDebrief from './RoundResultsMarketDebrief'
import RoundResultsCustomerSatisfaction from './RoundResultsCustomerSatisfaction'
import RoundResultsTotalShareholderReturn from './RoundResultsTotalShareholderReturn'
import RoundResultsReturnOnEquity from './RoundResultsReturnOnEquity'
import RoundResultsCustomers from './RoundResultsCustomers'
import RoundResultsCreditRating from './RoundResultsCreditRating'
import RoundResultsLoanImpairmentExpense from './RoundResultsLoanImpairmentExpense'
import RoundResultsEmployeeEngagement from './RoundResultsEmployeeEngagement'
import RoundResultsCustomerComplaints from './RoundResultsCustomerComplaints'
import RoundResultsReputationIndex from './RoundResultsReputationIndex'
import RoundResultsRiskIncidents from './RoundResultsRiskIncidents'
import RoundResultsRegulatorSanctions from './RoundResultsRegulatorSanctions'
import RoundResultsIncrementalFTE from './RoundResultsIncrementalFTE'
import RoundResultsIncrementalFTECosts from './RoundResultsIncrementalFTECosts'
import RoundResultsBusinessLoanLosses from './RoundResultsBusinessLoanLosses'
import RoundResultsOverallWinningMetric from './RoundResultsOverallWinningMetric'
import RoundResultsWholesaleFundingCostsAllRatings from './RoundResultsWholesaleFundingCostsAllRatings'
import RoundResultsWholesaleFundingCostsARatings from './RoundResultsWholesaleFundingCostsARatings'
import RoundResultsWholesaleFundingIssues from './RoundResultsWholesaleFundingIssues'
import RoundResultsNPATRoe from './RoundResultsNPATRoe'
import RoundResultsExecutiveDecisionsTable from './RoundResultsExecutiveDecisionsTable'
import RoundResultsExecutiveDecisionsTableTwo from './RoundResultsExecutiveDecisionsTableTwo'
import RoundResultsDealRoomTable from './RoundResultsDealRoomTable'
import RoundResultsBankBailOuts from './RoundResultsBankBailOuts'
import RoundResultsRegulatoryCapitalInterventions from './RoundResultsRegulatoryCapitalIntervention'
import RoundResultsRiskIncidentsClosing from './RoundResultsRiskIncidentsClosing'
import RoundResultsRoyalCommission from './RoundResultsRoyalCommission'
import RoundResultsNeoBankAmlIssue from './RoundResultsNeoBankAmlIssue'
import RoundResultsNewsRoom from './RoundResultsNewsRoom'
import RoundResultsDealRoomInformation from './RoundResultsDealRoomInformation'
import RoundResultsNetInterestMargin from './RoundResultsNetInterestMargin'
import RoundResultsCustomText from './RoundResultsCustomText'
import RoundResultsCustomImage from './RoundResultsCustomImage'
import { fakeRiskIncidentsClosing } from './fakeData'
import Theme from '../../../styles/_theme.module.scss'
import InlineGroup from '../../atoms/inlinegroup/InlineGroup'
import Button from '../../atoms/button/Button'
import ResultsHeader from './header/ResultsHeader'
import { ResultsContext, useResultsContextValue } from './context/context'
import { reducer } from './context/state'
import RoundResultsTeamSummary from './RoundResultsTeamSummary'
import RoundResultsCeoTransfer from './RoundResultsCeoTransfer'
import RoundResultsReversion from './RoundResultsReversion'
import RoundResultsSurvey from './RoundResultsSurvey'
import RoundResultsThankYou from './RoundResultsThankYou'

const colours = [
  Theme.chart0,
  Theme.chart1,
  Theme.chart2,
  Theme.chart3,
  Theme.chart4,
  Theme.chart5,
  Theme.chart6,
  Theme.chart7,
  Theme.chart8,
  Theme.chart9
]

const cpuColours = [
  Theme.pc1,
  Theme.pc2,
  Theme.pc3
]

export interface TeamsWithColours extends ModelAPI.Reporting.ReportingTeams {
  colour: string
}

interface Props {
  roundNumber: number
  data: ModelAPI.Reporting.ReportingResults
  onlyThesePages: API.ReportingLayoutType[]
  startingLayout?: API.ReportingLayoutType
  hidePagination?: boolean
  hideHeader?: boolean
  hideTeamPositions?: boolean
  noAnimations?: boolean
  allowCustomTemplateEdits?: boolean
  onCustomTemplateEdit?: (val: API.ReportingTemplateLayoutUpdateRequest) => void
}

const noop = () => {}

const RoundResultsPage: React.FC<Props> = ({ roundNumber, data, onlyThesePages, hidePagination, hideHeader, hideTeamPositions, noAnimations, startingLayout, allowCustomTemplateEdits, onCustomTemplateEdit }) => {
  const pastRound1 = roundNumber > 1
  const [pageIndex, setPageIndex] = useState(0)
  const [renderKey, setRenderKey] = useState(1)

  const [state, dispatch] = useReducer(reducer, {
    hideTeamPositions: hideTeamPositions ?? false,
    noAnimations: noAnimations ?? false,
    allowCustomTemplateEdits: allowCustomTemplateEdits ?? false,
    onCustomTemplateEdit: onCustomTemplateEdit ?? noop
  })

  const teams: TeamsWithColours[] = useMemo(() => {
    if (!data) {
      return []
    }
    return data.teams.map((team, idx) => {
      return {
        ...team,
        colour: team.isCpuTeam ? cpuColours[idx % cpuColours.length] : colours[idx % colours.length]
      }
    })
  }, [data])

  const pages: API.ReportingTemplateLayoutResponse[] = useMemo(() => {
    if (!data) {
      return []
    } else {
      return data.layouts.filter(page => !!page && page.enabled && (!onlyThesePages.length || onlyThesePages.includes(page.type)))
    }
  }, [data, onlyThesePages])

  useEffect(() => {
    if (startingLayout) {
      const index = pages.findIndex(l => l.type === startingLayout)
      if (index > -1) {
        setPageIndex(index)
      }
    }
  }, [pages, startingLayout])

  const currentPage = useMemo(() => {
    return pages[pageIndex]
  }, [pageIndex, pages])

  const resultsContext = useResultsContextValue(state, dispatch)

  if (currentPage.type === 'summary') {
    return (
      <div className='results-container results-container-summary'>
        <div className='results' style={{ flex: 1 }}>
          <VerticalGroup full center key={'render' + renderKey}>
            <VerticalGroup wide full className="pt-5 pb-10 pr-10 pl-10">
              <RoundResultsSummary logo={data.logo} gameName={data.gameName} round={roundNumber} handleClick={() => setPageIndex(pageIndex + 1)} />
            </VerticalGroup>
          </VerticalGroup>
        </div>
      </div>
    )
  }

  let maxHeight = 'calc(100% - 64px)'
  if (hideHeader || hidePagination) {
    maxHeight = '100%'
  }


  return (
    <ResultsContext.Provider value={resultsContext}>
      <div className='results-container results-gradient' style={{ flex: 1, justifyContent: 'space-between', flexDirection: 'column', width: '100%' }}>
        {!hideHeader && <ResultsHeader logo={data.logo} gameName={data?.gameName ?? ''} round={roundNumber} />}
        <div style={{ flex: 1, justifyContent: 'space-between', flexDirection: 'column', display: 'flex', maxHeight: hidePagination ? '100%' : 'calc(100% - 64px)' }} key={'render' + renderKey}>
          <VerticalGroup wide className="pt-5 pb-5 pr-10 pl-10" full style={{ maxHeight }}>
            {currentPage.type === 'market_debrief' && <RoundResultsMarketDebrief data={data.marketDebrief} />}
            {currentPage.type === 'customer_satisfaction' &&  <RoundResultsCustomerSatisfaction pastRound1={pastRound1} teams={teams} customerSatisfaction={data.customerSatisfaction} />}
            {currentPage.type === 'total_shareholder_return' &&  <RoundResultsTotalShareholderReturn pastRound1={pastRound1} teams={teams} shareholderReturn={data.shareholderReturn} />}
            {currentPage.type === 'return_on_equity' &&  <RoundResultsReturnOnEquity pastRound1={pastRound1} teams={teams} returnOnEquity={data.returnOnEquity} />}
            {currentPage.type === 'customers' &&  <RoundResultsCustomers pastRound1={pastRound1} teams={teams} customers={data.customers} />}
            {currentPage.type === 'credit_rating' &&  <RoundResultsCreditRating pastRound1={pastRound1} teams={teams} creditRating={data.creditRating} />}
            {currentPage.type === 'loan_impairment_expense' &&  <RoundResultsLoanImpairmentExpense pastRound1={pastRound1} teams={teams} loanImpairmentExpense={data.loanImpairmentExpense} />}
            {currentPage.type === 'employee_engagement' &&  <RoundResultsEmployeeEngagement pastRound1={pastRound1} teams={teams} employeeEngagement={data.employeeEngagement} />}
            {currentPage.type === 'customer_complaints' &&  <RoundResultsCustomerComplaints pastRound1={pastRound1} teams={teams} customerComplaints={data.customerComplaints} />}
            {currentPage.type === 'reputation_index' &&  <RoundResultsReputationIndex pastRound1={pastRound1} teams={teams} repuationIndex={data.reputationIndex} />}
            {currentPage.type === 'risk_incidents' &&  <RoundResultsRiskIncidents pastRound1={pastRound1} teams={teams} riskIncidents={data.riskIncidents} />}
            {currentPage.type === 'regulator_sanctions' &&  <RoundResultsRegulatorSanctions pastRound1={pastRound1} teams={teams} regulatorSanctions={data.regulatorSanctions} />}
            {currentPage.type === 'incremental_fte' &&  <RoundResultsIncrementalFTE pastRound1={pastRound1} teams={teams} incrementalFTE={data.incrementalFTE} />}
            {currentPage.type === 'incremental_fte_costs' &&  <RoundResultsIncrementalFTECosts pastRound1={pastRound1} currencyDenomination={data.currencyDenomination} teams={teams} incrementalFTECosts={data.incrementalFTECosts} key={renderKey} parentKey={renderKey} />}
            {currentPage.type === 'business_loan_losses' &&  <RoundResultsBusinessLoanLosses pastRound1={pastRound1} teams={teams} businessLoanLosses={data.businessLoanLosses} />}
            {currentPage.type === 'overall_winning_metric' &&  <RoundResultsOverallWinningMetric pastRound1={pastRound1} teams={teams} overallWinningMetric={data.overallWinner} />}
            {currentPage.type === 'wholesale_funding_costs_all_ratings' &&  <RoundResultsWholesaleFundingCostsAllRatings pastRound1={pastRound1} teams={teams} wholesaleFundingCostsAllRatings={data.wsfCostsAllRatings} />}
            {currentPage.type === 'wholesale_funding_costs_a_ratings' &&  <RoundResultsWholesaleFundingCostsARatings pastRound1={pastRound1} teams={teams} wholesaleFundingCostsARatings={data.wsfCostsARatings} />}
            {currentPage.type === 'wholesale_funding_issues' &&  <RoundResultsWholesaleFundingIssues currencyDenomination={data.currencyDenomination} teams={teams} wholesaleFundingIssues={data.wholesaleFundingIssues} />}
            {currentPage.type === 'npat_&_roe' && <RoundResultsNPATRoe currencyDenomination={data.currencyDenomination} teams={teams} npatRoe={data.npatAndRoe} />}
            {currentPage.type === 'executive_decisions_table' && <RoundResultsExecutiveDecisionsTable executiveDecisions={data.excoDecision!} />}
            {currentPage.type === 'executive_decisions_table_two' && <RoundResultsExecutiveDecisionsTableTwo executiveDecisions={data.excoDecision!} teams={teams} />}
            {currentPage.type === 'deal_room_results' && <RoundResultsDealRoomTable dealRoom={data.dealRoom!} teams={teams} />}
            {currentPage.type === 'bank_bail_outs' && <RoundResultsBankBailOuts data={data.bankBailouts} teams={teams} />}
            {currentPage.type === 'regulatory_capital_intervention' && <RoundResultsRegulatoryCapitalInterventions data={data.regulatoryCapitalIntervention} teams={teams} />}
            {currentPage.type === 'risk_incidents_closing' && <RoundResultsRiskIncidentsClosing riskIncidentsClosing={fakeRiskIncidentsClosing} teams={teams} currencyDenomination={data.currencyDenomination} />}
            {currentPage.type === 'royal_commission' && <RoundResultsRoyalCommission teams={teams} currencyDenomination={data.currencyDenomination} data={data.royalCommissionEvent!} />}
            {currentPage.type === 'neobank_aml_issue' && <RoundResultsNeoBankAmlIssue teams={teams} amlEvent={data.amlEvent!} currencyDenomination={data.currencyDenomination}/>}
            {currentPage.type === 'news_room' && <RoundResultsNewsRoom round={String(roundNumber + 1)} data={data.newsRoom!} />}
            {currentPage.type === 'deal_room_information' && <RoundResultsDealRoomInformation data={data.nextDealRoom!} />}
            {currentPage.type === 'net_interest_margin' && <RoundResultsNetInterestMargin pastRound1={pastRound1} teams={teams} marketMargins={data.marketMargins} />}
            {currentPage.type === 'custom_text' && <RoundResultsCustomText data={currentPage.config} />}
            {currentPage.type === 'custom_image' && <RoundResultsCustomImage data={currentPage.config} />}
            {currentPage.type === 'team_summary' && <RoundResultsTeamSummary data={data.teams} />}
            {currentPage.type === 'ceo_transfer' && <RoundResultsCeoTransfer />}
            {currentPage.type === 'reversion' && <RoundResultsReversion />}
            {currentPage.type === 'survey' && <RoundResultsSurvey />}
            {currentPage.type === 'thank_you' && <RoundResultsThankYou />}
          </VerticalGroup>
          {!hidePagination && <InlineGroup className='mt-3 pb-10 pr-10 pl-10' spread block>
            <Button shadow outline onClick={() => setPageIndex(pageIndex - 1)}>Back</Button>
            <InlineGroup>
              {pageIndex <= pages.length - 1 && <Button outline shadow onClick={() => setRenderKey(renderKey + 1)}>Refresh</Button>}
              {pageIndex < pages.length - 1  && <Button className='ml-4' border shadow onClick={() => setPageIndex(pageIndex + 1)}>Next</Button>}
            </InlineGroup>
          </InlineGroup>}
        </div>
      </div>
    </ResultsContext.Provider>
  )
}

export default RoundResultsPage